/**
 * Capture/output image sizing and cost estimation for the `gpt-image-2` endpoint.
 *
 * The API's size constraints are easy to violate silently — an edge that is not a multiple of 16, or
 * a total pixel count a hair under the minimum, comes back as an opaque 400 after the user has
 * already waited. Everything here is pure so those rules are unit-tested rather than discovered in
 * production.
 */

import { estimateImageCostUsd } from '@shared/ai/pricing';

export interface ImageSize {
  width: number;
  height: number;
}

export type RenderQualityTier = 'low' | 'medium' | 'high';

export const RENDER_QUALITY_TIERS: readonly RenderQualityTier[] = ['low', 'medium', 'high'];

/** Edges must be multiples of this. */
export const EDGE_MULTIPLE = 16;
/** Inclusive total-pixel bounds accepted by the endpoint. */
export const MIN_PIXELS = 655_360;
export const MAX_PIXELS = 8_294_400;
/** Neither edge may exceed this. */
export const MAX_EDGE = 3840;
/** Long edge / short edge may not exceed this. */
export const MAX_ASPECT = 3;

/**
 * The sizes documented in OpenAI's size table. Arbitrary 16-divisible sizes are reported to work,
 * but only these are contractually guaranteed, so they are what the UI offers by default.
 */
export const SIZE_PRESETS: readonly { id: string; label: string; size: ImageSize }[] = [
  { id: 'landscape', label: 'Landscape · 1536×1024', size: { width: 1536, height: 1024 } },
  { id: 'square', label: 'Square · 1024×1024', size: { width: 1024, height: 1024 } },
  { id: 'portrait', label: 'Portrait · 1024×1536', size: { width: 1024, height: 1536 } },
];

/** Serialize a size for the API's `size` field. */
export function formatSize(size: ImageSize): string {
  return `${size.width}x${size.height}`;
}

/**
 * The documented size whose aspect ratio is closest to the viewport.
 *
 * `fitCaptureSize` will happily derive something like 1584×992 — legal by every published rule, but
 * not in the endpoint's size table, and an unlisted size is a 400 discovered only after the user has
 * paid for the wait. "Fit" therefore snaps to a guaranteed size; the letterbox in photo mode shows
 * the resulting crop, so the framing stays honest.
 */
export function nearestSizePreset(viewportWidth: number, viewportHeight: number): ImageSize {
  const fallback = SIZE_PRESETS[0] as { size: ImageSize };
  const safeWidth = Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : 1;
  const safeHeight = Number.isFinite(viewportHeight) && viewportHeight > 0 ? viewportHeight : 1;
  const target = safeWidth / safeHeight;

  let best = fallback.size;
  let bestDelta = Infinity;
  for (const preset of SIZE_PRESETS) {
    // Compare in log space so 2:1 and 1:2 are judged equally far from square.
    const delta = Math.abs(Math.log(preset.size.width / preset.size.height) - Math.log(target));
    if (delta < bestDelta) {
      bestDelta = delta;
      best = preset.size;
    }
  }
  return best;
}

function roundToMultiple(value: number, multiple: number): number {
  return Math.max(multiple, Math.round(value / multiple) * multiple);
}

/** Why a size was rejected, or null when it is acceptable. */
export function validateSize(size: ImageSize): string | null {
  const { width, height } = size;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
    return 'Width and height must be positive integers.';
  }
  if (width % EDGE_MULTIPLE !== 0 || height % EDGE_MULTIPLE !== 0) {
    return `Width and height must be multiples of ${EDGE_MULTIPLE}.`;
  }
  if (width > MAX_EDGE || height > MAX_EDGE) {
    return `Neither edge may exceed ${MAX_EDGE}px.`;
  }
  const pixels = width * height;
  if (pixels < MIN_PIXELS) {
    return `Total pixels must be at least ${MIN_PIXELS.toLocaleString()}.`;
  }
  if (pixels > MAX_PIXELS) {
    return `Total pixels must not exceed ${MAX_PIXELS.toLocaleString()}.`;
  }
  const aspect = Math.max(width, height) / Math.min(width, height);
  if (aspect > MAX_ASPECT + 1e-9) {
    return `Aspect ratio must be between 1:${MAX_ASPECT} and ${MAX_ASPECT}:1.`;
  }
  return null;
}

export function isValidSize(size: ImageSize): boolean {
  return validateSize(size) === null;
}

/**
 * Derive a legal capture size that matches the viewport's aspect ratio as closely as the constraints
 * allow, targeting roughly `targetPixels` in total.
 *
 * The aspect is clamped to 3:1 first, then the two edges are scaled to hit the pixel target, rounded
 * to the 16px lattice, and finally nudged until every rule holds — rounding can push a borderline
 * size back over an edge, so the result is re-checked rather than assumed.
 */
export function fitCaptureSize(
  viewportWidth: number,
  viewportHeight: number,
  targetPixels = 1_572_864,
): ImageSize {
  const safeWidth = Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : 16;
  const safeHeight = Number.isFinite(viewportHeight) && viewportHeight > 0 ? viewportHeight : 16;

  const rawAspect = safeWidth / safeHeight;
  const aspect = Math.min(MAX_ASPECT, Math.max(1 / MAX_ASPECT, rawAspect));
  const pixels = Math.min(MAX_PIXELS, Math.max(MIN_PIXELS, targetPixels));

  // width * height = pixels, width / height = aspect.
  let width = roundToMultiple(Math.sqrt(pixels * aspect), EDGE_MULTIPLE);
  let height = roundToMultiple(Math.sqrt(pixels / aspect), EDGE_MULTIPLE);

  width = Math.min(width, MAX_EDGE);
  height = Math.min(height, MAX_EDGE);

  // Grow the smaller edge until the pixel floor is met; shrink the larger one under the ceiling.
  // Both loops are bounded by the edge limits, so neither can spin.
  let guard = 0;
  while (width * height < MIN_PIXELS && guard < 512) {
    if (width <= height && width + EDGE_MULTIPLE <= MAX_EDGE) width += EDGE_MULTIPLE;
    else if (height + EDGE_MULTIPLE <= MAX_EDGE) height += EDGE_MULTIPLE;
    else if (width + EDGE_MULTIPLE <= MAX_EDGE) width += EDGE_MULTIPLE;
    else break;
    guard += 1;
  }
  guard = 0;
  while (width * height > MAX_PIXELS && guard < 512) {
    if (width >= height) width -= EDGE_MULTIPLE;
    else height -= EDGE_MULTIPLE;
    guard += 1;
  }

  // Rounding may have pushed the aspect past 3:1 on extreme viewports; pull the long edge back in.
  guard = 0;
  while (Math.max(width, height) / Math.min(width, height) > MAX_ASPECT && guard < 512) {
    if (width > height) width -= EDGE_MULTIPLE;
    else height -= EDGE_MULTIPLE;
    guard += 1;
  }

  const candidate = { width, height };
  // Fall back to the guaranteed landscape preset rather than emit something the API will reject.
  return isValidSize(candidate) ? candidate : { width: 1536, height: 1024 };
}

/**
 * Estimated USD cost of a render request. Advisory only — the API is the authority on billing.
 *
 * The table itself now lives in `shared/ai/pricing.ts`, because the server needs the same
 * arithmetic to write a cost into the usage ledger. Keeping a second copy here would mean the
 * figure shown in the enhance panel before a render and the figure recorded after it could drift
 * apart, which is the one thing an estimate must not do.
 */
export function estimateCostUsd(size: ImageSize, quality: RenderQualityTier, count = 1): number {
  return estimateImageCostUsd(size, quality, count);
}

/** Format an estimate for the UI: sub-cent values still need to read as money. */
export function formatCostUsd(usd: number): string {
  if (usd < 0.01) return `<$0.01`;
  return `$${usd.toFixed(2)}`;
}
